<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <DefineNav>
        <nav class="flex flex-col gap-1 p-3">
            <template v-for="section in sections" :key="section.labelKey || 'top'">
                <!-- The heading is a LABEL and not a link. „Skupina“ is where the group
                     is kept rather than a place to go, and a heading that could be
                     pressed is a heading somebody presses. -->
                <p
                    v-if="section.labelKey"
                    class="mt-3 px-3 pb-1 text-[0.65rem] font-semibold uppercase tracking-wide text-dimmed"
                >
                    {{ t(section.labelKey) }}
                </p>

                <UButton
                    v-for="item in section.items"
                    :key="item.name"
                    :to="{ name: item.name }"
                    :label="t(item.labelKey)"
                    :title="t(item.labelKey)"
                    :icon="item.icon"
                    :color="route.name === item.name ? 'primary' : 'neutral'"
                    :variant="route.name === item.name ? 'subtle' : 'ghost'"
                    size="lg"
                    class="justify-start"
                    @click="closeMobileMenu"
                />
            </template>
        </nav>
    </DefineNav>

    <aside class="hidden h-full w-56 shrink-0 flex-col border-e border-default bg-default lg:flex">
        <div class="flex h-16 items-center gap-2 border-b border-default px-4">
            <UIcon name="i-lucide-building-2" class="size-6 shrink-0 text-primary" />
            <div class="flex min-w-0 flex-col leading-tight">
                <span class="truncate text-base font-semibold text-highlighted">{{ $t('app.name') }}</span>
                <span class="truncate text-xs text-dimmed">{{ version }}</span>
            </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto">
            <ReuseNav />
        </div>

        <div class="border-t border-default px-4 py-3">
            <p class="truncate text-xs text-dimmed">{{ $t('app.product') }} · {{ $t('app.subtitle') }}</p>
        </div>
    </aside>

    <USlideover
        v-model:open="mobileMenuOpen"
        :title="$t('app.name')"
        :description="version"
        :ui="{ content: 'w-72 max-w-[85vw]' }"
        side="left"
    >
        <template #body>
            <ReuseNav />
        </template>
    </USlideover>
</template>

<script lang="ts" setup>
    import { createReusableTemplate } from '@vueuse/core'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { useLayout } from '@/layouts/layout'
    import { NAV_SECTIONS } from '@/libs/navigation'
    import { mayOpen } from '@/router'

    const { t }  = useI18n()
    const route  = useRoute()
    const router = useRouter()

    const { mobileMenuOpen, closeMobileMenu } = useLayout()

    const [DefineNav, ReuseNav] = createReusableTemplate()

    /** What this bundle was built as — a build-time constant, not a call. */
    const version = computed(() => __APP_VERSION__.startsWith('v') ? __APP_VERSION__ : `v${__APP_VERSION__}`)

    /**
     * The menu, filtered to what this account may actually open.
     *
     * # The list is not here
     *
     * It is `libs/navigation.ts` — the ONE ordered list the sidebar, the landing rule and
     * the permission sweep are all built from. The sidebar used to carry it, and the
     * landing rule carried nothing: it walked `router.getRoutes()` and handed back the
     * first route it could open, which is neither this order nor a list of screens, and
     * which sent a one-right account onto a DETAIL route it could not build an address
     * for. A menu keeping its own copy of the order is a menu that will one day disagree
     * with the rule about where somebody belongs.
     *
     * What is left here is the drawing: two sections, a quiet heading over the second,
     * and the filter below.
     *
     * # The right is the ROUTE's
     *
     * An entry that leads to a 403 is worse than no entry at all, so a screen appears only
     * where its right does — and the right is read from the ROUTE rather than from the
     * entry, although the entry declares it too. The guard is what actually refuses, so
     * the menu asks the same question the guard will. `tools/landing.test.mjs` fails the
     * moment the two declarations disagree, which is what makes it safe for the manifest
     * to carry one at all.
     *
     * A section whose every entry is filtered away draws neither its heading nor itself.
     */
    const sections = computed(() => NAV_SECTIONS.map(section => ({
        labelKey: section.labelKey,
        items:    section.items.filter(item => {
            const target = router.getRoutes().find(candidate => candidate.name === item.name)

            return !!target && mayOpen(target)
        }),
    })).filter(section => section.items.length))
</script>
